import Axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import CompanyPageLayout from '../../../components/CompanyPages/CompanyPageLayout/CompanyPageLayout';
import IndustryTable from '../../../components/Employer/Benefits/IndustryTable/IndustryTable';
import MobileIndustrialTable from '../../../components/Employer/Benefits/MobileIndustrialTable/MobileIndustrialTable';
import RowRecord from '../../../components/Employer/Benefits/RowRecord/RowRecord';
import { COMPANY_PROPTYPES } from '../../../components/Includes/CompanyProps';
/* COMPONENTS */
import PageSection from '../../../components/Layout/PageSection/PageSection';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    PAGE_TABS,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';
/* CONTEXTS */
import { JobContextProvider } from '../../../contexts/JobContext';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
/* CONSTANTS */
import {
    constructDescription,
    joinByCommaOrAnd,
    removeMissingMedians,
} from './constants';
/* STYLESHEETS */
import './EmployerParentalLeave.scss';

const modalImage = '/v1/icons/fgb-bubble-logo.png';
const continueButtonMeta = {
    buttonText: 'Continue',
    buttonType: 'purple-medium-white',
};

export class EmployerParentalLeave extends React.PureComponent {
    handleSubmit = async (fieldName, tip, comment) => {
        const payload = {
            inputName: fieldName,
            companyId: this.props.company.companyId,
            comment,
            tip,
        };

        const modalData = {
            buttonMetas: [continueButtonMeta],
            imageSrc: modalImage,
        };

        try {
            const { data: results } =
                (await Axios.post(this.props.submitUrl, payload)) || {};
            const { data } = results || {};
            modalData.subheading = data.response;

            this.props.modalTrigger('ConfirmationModal', modalData);
        } catch ({ response }) {
            const { data } = response || {};
            const { errors } = data || {};

            const [firstErrorKey] = Object.keys(errors);

            modalData.heading = 'Oops! Something went wrong!';

            if (firstErrorKey === '_general') {
                modalData.subheading =
                    'Something went wrong. Please try again!';
            } else {
                modalData.subheading = errors[firstErrorKey];
            }

            this.props.modalTrigger('ConfirmationModal', modalData);
        }
    };

    renderDeclaration = () => {
        const { companyName } = this.props.company;

        return (
            <div className="declaration">
                We crowdsource {companyName}&apos;s maternity, paternity and
                parental leave policies, based on {companyName}&apos;s employee
                reviews and anonymous tips from {companyName} employees.
            </div>
        );
    };

    renderCompanyLeavePolicies = () => {
        return (
            <section className="leave-policies content-box">
                <h3 className="sub-section-heading">
                    {this.props.company.companyName} Maternity and Paternity
                    Leave Policies
                </h3>
                {this.constructCompanyLeaveDescription()}
                {this.renderCompanyLeaveTable()}
            </section>
        );
    };

    renderIndustryLeavePolicies = () => {
        return (
            <section className="leave-policies content-box">
                <h3 className="sub-section-heading">
                    {this.props.industry} Maternity and Paternity Leave
                </h3>
                {this.constructIndustryLeaveDescription()}
                {this.renderIndustryLeaveTable()}
            </section>
        );
    };

    constructCompanyLeaveDescription = () => {
        return (
            <span className="description">
                {this.props.company.companyName} offers{' '}
                {this.constructLeaveSummary()}. This information is based on
                anonymous tips submitted by employees.
            </span>
        );
    };

    constructIndustryLeaveDescription = () => {
        return (
            <span className="description">
                How many weeks of paid maternity, unpaid maternity, paid
                paternity and unpaid paternity leave do employers in the{' '}
                {this.props.industry} industry offer?
            </span>
        );
    };

    constructLeaveSummary = () => {
        const { companyRecords } = this.props;

        return companyRecords
            .filter(removeMissingMedians)
            .map(constructDescription)
            .reduce(joinByCommaOrAnd, '');
    };

    renderCompanyLeaveTable = () => {
        const renderSingleRecord = (record, index) => {
            const {
                low,
                median,
                high,
                comments,
                type,
                description,
                tipInputType,
                fieldName,
            } = record;
            return (
                <RowRecord
                    key={`parental-leave-${index}`}
                    fieldName={fieldName}
                    low={low}
                    median={median}
                    high={high}
                    comments={comments}
                    type={type}
                    description={description}
                    tipInputType={tipInputType}
                    onCommentSubmit={this.handleSubmit}
                />
            );
        };

        return this.props.companyRecords.map(renderSingleRecord);
    };

    renderIndustryLeaveTable = () => {
        const { industryRecords } = this.props;
        const industryTableProps = {
            theads: ['Benefits', 'Median', 'Average'],
            rowRecords: industryRecords,
        };

        return (
            <React.Fragment>
                <div className="d-none d-md-block">
                    <IndustryTable {...industryTableProps} />
                </div>

                <div className="d-block d-md-none">
                    <MobileIndustrialTable {...industryTableProps} />
                </div>
            </React.Fragment>
        );
    };

    render() {
        return (
            <div className="page-Employer-EmployerParentalLeave">
                <SnowplowPageViewTracker
                    pageCompanyId={this.props.company.companyId}
                    pageEntityId={this.props.company.companyId}
                    pageSection={PAGE_VIEW_SECTIONS.COMPANY_PROFILE}
                    pageTab={PAGE_TABS.COMPANY_PARENTAL_LEAVE}
                    pageType={PAGE_VIEW_TYPES.CONTENT}
                />
                <JobContextProvider
                    content={[]}
                    userJobProfile={this.props.userJobProfile}
                >
                    <CompanyPageLayout company={this.props.company}>
                        <PageSection className="main-content">
                            <section>
                                <header>
                                    <div className="width-control">
                                        <h3 className="page-title">
                                            Parental Leave
                                        </h3>
                                        {this.renderDeclaration()}
                                    </div>
                                </header>
                                <div className="content-section">
                                    <div className="content-width-control">
                                        {this.renderCompanyLeavePolicies()}
                                        {this.renderIndustryLeavePolicies()}
                                    </div>
                                </div>
                            </section>
                        </PageSection>
                    </CompanyPageLayout>
                </JobContextProvider>
            </div>
        );
    }
}

EmployerParentalLeave.propTypes = {
    /**
     * object of company
     */
    company: PropTypes.shape(COMPANY_PROPTYPES).isRequired,
    /**
     * string of company's industry
     */
    industry: PropTypes.string.isRequired,
    /**
     * array of company parental leave records
     */
    companyRecords: PropTypes.arrayOf(PropTypes.string).isRequired,
    /**
     * array of industry parental leave records
     */
    industryRecords: PropTypes.arrayOf(PropTypes.string).isRequired,
    /**
     * string of submit url
     */
    modalTrigger: PropTypes.func.isRequired,
    submitUrl: PropTypes.string.isRequired,
    userJobProfile: PropTypes.shape({
        hash: PropTypes.string,
        isCompleted: PropTypes.bool,
    }),
};

EmployerParentalLeave.defaultProps = {
    userJobProfile: {},
};

export default (props) => (
    <PageConsumer>
        {({ modalTrigger }) => (
            <EmployerParentalLeave {...props} modalTrigger={modalTrigger} />
        )}
    </PageConsumer>
);
